import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  InjectionToken,
  Injector,
  Signal,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterLink,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  TwDropdownComponent,
  TwDropdownItemComponent,
  TwDropdownMenuComponent,
  TwDropdownTriggerDirective,
} from '@twist/ui-popovers';
import {
  ActiveTrailResolver,
  NavDescriptor,
  NavRegistry,
  normalizeSiblings,
  SIBLING_PROVIDER,
  SiblingNavEntry,
  SiblingProvider,
} from '@twist/ui-navigation';
import { EmptySecurityConstraints, SecurityConstraintsEvaluator } from '@twist/core';

/**
 * Structural contract the auto-walking breadcrumbs primitive consumes.
 *
 * Defined here (in `@twist/ui-primitives`) rather than imported from
 * `@twist/ui/components` because that import direction would close a
 * project-graph cycle. The legacy `BreadcrumbService` in
 * `@twist/ui/components` already implements this shape exactly — a
 * single-line provider in the layout shell binds the concrete service
 * to this token (see `PortalLayoutComponent.providers`).
 */
/**
 * Accessible label for the home-led leading crumb. Rendered as a home
 * glyph (icon-only) in the chrome, but kept as text for screen readers
 * and the non-chevron variants that have no icon affordance.
 */
export const HOME_CRUMB_LABEL = 'Home';

export interface BreadcrumbLabelResolver {
  /**
   * Read-only signal of custom-label overrides. The breadcrumbs primitive
   * touches this signal inside an `effect` so a `setLabel(...)` call from
   * a feature store re-runs the route walk without a navigation cycle.
   */
  readonly customLabels: Signal<ReadonlyMap<string, string>>;

  /**
   * Resolve the breadcrumb label for an activated-route snapshot.
   * Return `null` to skip the route entirely (no crumb appended).
   */
  getLabelForRoute(route: ActivatedRouteSnapshot): string | null;
}

/**
 * Injection token the auto-walker consumes to resolve crumb labels.
 *
 * Bind via `{ provide: BREADCRUMB_LABEL_RESOLVER, useExisting: BreadcrumbService }`
 * (the layout shell already does this); leave unprovided to disable the
 * `auto` mode (projection / `[breadcrumbs]` modes still work without it).
 */
export const BREADCRUMB_LABEL_RESOLVER = new InjectionToken<BreadcrumbLabelResolver>(
  'TwBreadcrumbLabelResolver',
);

/**
 * Crumb item shape consumed by `<tw-page-header-breadcrumbs [breadcrumbs]="…">`.
 *
 * `siblings` powers the **segment-switcher** (`variant: 'segment'`): when a
 * segment carries a sibling list, the header renders a `▾` affordance that
 * opens a `tw-dropdown` of sibling pages (current segment checkmarked) for
 * same-page navigation. Siblings may be full `TwBreadcrumbItem`s (with their
 * own `url`) or bare `{ label }` markers — selecting a sibling that has a
 * `url` navigates; selecting the current one is a no-op.
 */
export interface TwBreadcrumbItem {
  /** Display label rendered inside the anchor (or in the trailing span for the current crumb). */
  label: string;
  /** URL fragment passed to `routerLink`. Optional — the trailing crumb may omit it. */
  url?: string;
  /** Optional query-param map forwarded to `routerLink`. */
  queryParams?: Record<string, string | number | boolean | null | undefined>;
  /** Optional icon ref for the `icon` variant (resolved by the host's icon system). */
  icon?: string;
  /**
   * Marks this item as the currently-active sibling inside a parent's
   * `siblings` list. The segment-switcher checkmarks the sibling whose
   * `current` is true (falling back to label-equality with the owning
   * crumb when no sibling carries the flag).
   */
  current?: boolean;
  /**
   * Optional sibling pages for the segment-switcher. When present, the
   * segment renders a `▾` → `tw-dropdown` of these siblings for same-page
   * navigation; the current segment is checkmarked. Bare `{ label }`
   * siblings render without navigation. In the default/chevron variant
   * this auto-promotes the crumb to a ▾ switcher (no opt-in needed).
   */
  siblings?: TwBreadcrumbItem[];
}

/**
 * Internal walker record: the built crumb plus the metadata needed by the
 * post-walk registry-sibling enrichment (nearest declared `navId` and the
 * accumulated url that segment-relative sibling urls are built from).
 */
interface BuiltCrumb {
  item: TwBreadcrumbItem;
  navId: string | undefined;
  baseUrl: string;
}

/** Breadcrumb rendering treatment. `segment` is the unified portal default. */
export type TwBreadcrumbVariant =
  | 'chevron'
  | 'slash'
  | 'icon'
  | 'pill'
  | 'segment'
  | 'collapse';

/**
 * `tw-page-header-breadcrumbs` — owned, elevated **lead** crumb row for
 * {@link import('./tw-page-header.component').TwPageHeaderComponent}.
 *
 * The breadcrumb is now the first, most prominent band of the header — it
 * replaces the cut eyebrow + back button as the page's orientation anchor.
 *
 * Render modes with explicit precedence (highest first):
 *
 * 1. **`[breadcrumbs]` input wins** — pre-shaped crumb list.
 * 2. **Projection wins** — author the crumb DOM directly.
 * 3. **`auto` walks routes** — for the layout shell (Router +
 *    {@link BreadcrumbLabelResolver}).
 *
 * Powered modes via the `variant` input:
 *
 * - `segment` (default) — unified portal breadcrumb: home-led, hover
 *   background on every crumb, design sizing; any segment with `siblings`
 *   shows a `▾` → `tw-dropdown` for same-page sibling navigation (current
 *   segment checkmarked). The ▾ stays latent until a crumb has siblings.
 * - `chevron` — home-led, refined current-page emphasis (plain text trail).
 * - `slash` — `/`-separated trail.
 * - `icon` — icon-led segments.
 * - `pill` — pill-styled segments.
 * - `collapse` — long trail → `first › … › parent › current`; the `…`
 *   opens a `tw-dropdown` revealing the hidden middle segments.
 *
 * Styling lives in the parent `tw-page-header.component.scss` via the
 * `.tw-page-header-breadcrumbs` BEM block — `ViewEncapsulation.None` lets
 * those rules cross the component boundary.
 */
@Component({
  selector: 'tw-page-header-breadcrumbs',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    RouterLink,
    TwDropdownComponent,
    TwDropdownTriggerDirective,
    TwDropdownMenuComponent,
    TwDropdownItemComponent,
  ],
  templateUrl: './tw-page-header-breadcrumbs.component.html',
  host: {
    '[class]': 'classes()',
    'aria-label': 'Breadcrumb',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TwPageHeaderBreadcrumbsComponent {
  /**
   * Pre-shaped crumb list. When provided (non-null and non-empty), this
   * input wins over both projection and auto-walking.
   */
  readonly breadcrumbs = input<TwBreadcrumbItem[] | null>(null);

  /**
   * When `true`, the component subscribes to router navigation and walks
   * the activated-route snapshot via the {@link BreadcrumbLabelResolver}
   * bound to the {@link BREADCRUMB_LABEL_RESOLVER} token. Used by the
   * portal layout shell.
   */
  readonly auto = input<boolean, unknown>(false, { transform: booleanAttribute });

  /**
   * Rendering treatment. Defaults to `segment` — the unified portal
   * breadcrumb: home-led, a hover background on EVERY crumb, the design
   * sizing (26px crumb / 13px / radius 7), and a latent ▾ sibling-switcher
   * that only appears when a crumb actually carries siblings. Every page
   * rendering `<tw-page-header-breadcrumbs auto>` without an explicit variant
   * inherits this unified look. The other variants (`chevron` / `slash` /
   * `icon` / `pill` / `collapse`) remain available as opt-in overrides.
   */
  readonly variant = input<TwBreadcrumbVariant>('segment');

  private readonly router = inject(Router, { optional: true });
  private readonly breadcrumbService = inject(BREADCRUMB_LABEL_RESOLVER, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  // ---- registry-fed sibling enrichment (auto mode only) ----
  private readonly registry = inject(NavRegistry, { optional: true });
  private readonly activeTrail = inject(ActiveTrailResolver, { optional: true });
  // The SecurityConstraintsEvaluator is `providedIn: 'root'` and pulls in
  // the full auth chain on construction; resolving it eagerly here would
  // break the many projection/[items] specs that never set up auth. Inject
  // the Injector instead and resolve the evaluator LAZILY inside
  // `resolveAllowedNavIds`, guarded by try/catch — so contexts without
  // auth simply skip the security filter (siblings stay unfiltered/empty).
  private readonly injector = inject(Injector);

  /**
   * Data-sibling providers (multi, optional). Each is normalized once into
   * a `Signal<SiblingNavEntry[]>` (array | Signal | Observable | Promise →
   * Signal) and indexed by its `navId` so the walk can bind provider
   * results to the matching crumb. Resolved in a field initialiser so the
   * Observable→Signal `toSignal` runs inside this injection context.
   */
  private readonly siblingProviders =
    (inject(SIBLING_PROVIDER, { optional: true }) as unknown as SiblingProvider[] | null) ?? [];

  /** Memoised normalized sibling signal per provider (index-aligned). */
  private readonly normalizedProviders: ReadonlyArray<{
    navId?: string;
    siblings: Signal<SiblingNavEntry[]>;
  }> = this.siblingProviders.map((p) => ({
    navId: p.navId,
    siblings: normalizeSiblings(p.siblings()),
  }));

  /**
   * Navigation node ids the current user is allowed to see, pre-resolved
   * from `descriptor(id).security` through the SAME async
   * `SecurityConstraintsEvaluator` the sidebar/palette use. The walker is
   * sync, so the async result is bridged into this signal: a crumb's
   * structural siblings are filtered to this set, and an effect re-walks
   * when it (re)populates — e.g. on org change. Starts empty (nothing
   * allowed) and widens as access resolves.
   */
  private readonly allowedNavIds = signal<ReadonlySet<string>>(new Set());

  /** Auto-walked crumb list — populated when `auto` is true and the router is present. */
  private readonly autoItems = signal<TwBreadcrumbItem[]>([]);

  protected readonly classes = computed(() =>
    ['tw-page-header-breadcrumbs', `tw-page-header-breadcrumbs--${this.variant()}`].join(' '),
  );

  /**
   * The crumb list to render. `[breadcrumbs]` input wins; otherwise the
   * auto-walked list. When both are empty the template falls through to
   * `<ng-content>` projection.
   */
  protected readonly resolvedItems = computed<TwBreadcrumbItem[]>(() => {
    const explicit = this.breadcrumbs();
    if (explicit && explicit.length > 0) {
      return explicit;
    }
    return this.autoItems();
  });

  /** Whether the resolved list should render via the data-driven template. */
  protected readonly hasItems = computed(() => this.resolvedItems().length > 0);

  // ---- collapse-mode partitioning (first › … › parent › current) ----
  protected readonly collapseHead = computed(() => this.resolvedItems()[0] ?? null);
  protected readonly collapseMiddle = computed(() => {
    const items = this.resolvedItems();
    return items.length > 3 ? items.slice(1, -2) : [];
  });
  protected readonly collapseTail = computed(() => {
    const items = this.resolvedItems();
    return items.length > 3 ? items.slice(-2) : items.slice(1);
  });

  /** Default leading-glyph rotation for the `icon` variant. */
  protected readonly iconFallbacks = ['home', 'layers', 'database', 'grid', 'settings'];

  constructor() {
    const router = this.router;
    const breadcrumbService = this.breadcrumbService;

    if (router && breadcrumbService) {
      router.events
        .pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => this.refreshAutoItems(router, breadcrumbService));

      effect(() => {
        breadcrumbService.customLabels();
        this.refreshAutoItems(router, breadcrumbService);
      });
    }

    effect(() => {
      if (this.auto() && router && breadcrumbService) {
        this.refreshAutoItems(router, breadcrumbService);
      } else if (!this.auto()) {
        this.autoItems.set([]);
      }
    });

    // Re-walk whenever the registry tree changes (descriptors loaded) or
    // the pre-resolved allowed-id set widens (access resolved / org
    // change) — structural siblings + their security filter depend on
    // both. Reading `registry.tree()` here makes the walk reactive to
    // registry mutations; reading `allowedNavIds()` re-runs after the
    // async access resolution lands.
    if (this.registry || this.normalizedProviders.length > 0) {
      effect(() => {
        this.registry?.tree();
        this.allowedNavIds();
        // Touch every data-sibling signal so a provider update (e.g. a
        // store loading its versions) re-walks and re-merges.
        for (const p of this.normalizedProviders) {
          p.siblings();
        }
        if (this.auto() && router && breadcrumbService) {
          this.refreshAutoItems(router, breadcrumbService);
        }
      });
    }

    // Bridge the async security evaluator into a sync-readable signal.
    // Resolve access for every breadcrumb-surface descriptor once; the
    // walk reads the resulting set synchronously. Re-resolve on org
    // change so siblings the user lost/gained access to update.
    if (this.registry) {
      void this.resolveAllowedNavIds();
      // No direct org signal is exposed here; re-resolve on each
      // NavigationEnd (cheap — gated by the evaluator's auth promise) so a
      // mid-session org switch reflects without a full reload.
      if (router) {
        router.events
          .pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe(() => void this.resolveAllowedNavIds());
      }
    }
  }

  /**
   * Pre-resolve which breadcrumb-surface descriptors the current user can
   * access, mirroring the palette/sidebar `*authGuard` semantics: a
   * descriptor with no `security` is authenticated-only
   * (EmptySecurityConstraints). Writes the allowed-id set, which the sync
   * walker filters structural siblings against.
   */
  private async resolveAllowedNavIds(): Promise<void> {
    const registry = this.registry;
    if (!registry) {
      return;
    }
    // Lazily resolve the (root) evaluator; in auth-less test/host contexts
    // its construction throws — swallow and leave the allowed set empty so
    // the walk degrades gracefully (no structural siblings rendered).
    let evaluator: SecurityConstraintsEvaluator;
    try {
      evaluator = this.injector.get(SecurityConstraintsEvaluator);
    } catch {
      return;
    }
    // Candidate ids = every node that opts into the breadcrumb surface.
    const candidates = registry.pageIndex(undefined, 'breadcrumb');
    const allowed = new Set<string>();
    await Promise.all(
      candidates.map(async (entry) => {
        const security = registry.descriptor(entry.id)?.security;
        const merged = { ...EmptySecurityConstraints, ...security };
        if (await evaluator.evaluate(merged)) {
          allowed.add(entry.id);
        }
      }),
    );
    this.allowedNavIds.set(allowed);
  }

  private refreshAutoItems(router: Router, breadcrumbService: BreadcrumbLabelResolver): void {
    if (!this.auto()) {
      return;
    }
    const root = router.routerState.snapshot.root;
    const built: BuiltCrumb[] = [];
    this.buildCrumbs(root, breadcrumbService, '', built);
    const items = built.map((b) => b.item);

    // Synthesize the active-tab crumb from the deepest primary route's
    // segment (no route-data change needed — see installations tabs) and
    // attach structural + (later) data siblings from the registry.
    this.enrichWithRegistrySiblings(root, built, items);

    // Home-led trail (design spec: `Home › … › current`). Prepend a Home
    // crumb that links to the app root whenever the walk produced a real
    // trail. When the walk yields NO crumbs (the app root, or a route with
    // neither breadcrumb nor a resolving navId) we leave `items` empty so
    // the host falls through to projection — we never render a lone Home
    // crumb, and never one that links to the current page.
    if (items.length > 0) {
      items.unshift({
        label: HOME_CRUMB_LABEL,
        icon: 'home',
        url: '/',
      });
    }

    this.autoItems.set(items);
  }

  /**
   * Walk the activated-route snapshot tree, depth-first into the primary
   * outlet only, accumulating crumb items per the
   * `BreadcrumbLabelResolver.getLabelForRoute(...)` contract. Records the
   * nearest declared `navId` and the accumulated url alongside each crumb
   * (in `built`) so the post-walk enrichment can bind registry siblings.
   */
  private buildCrumbs(
    route: ActivatedRouteSnapshot,
    breadcrumbService: BreadcrumbLabelResolver,
    url = '',
    built: BuiltCrumb[] = [],
    navId: string | undefined = undefined,
  ): BuiltCrumb[] {
    const children = route.children;
    if (children.length === 0) {
      return built;
    }

    for (const child of children) {
      if (child.outlet !== 'primary') {
        continue;
      }

      const routeURL = child.url.map(segment => segment.path).join('/');
      if (routeURL) {
        url += `/${routeURL}`;
      }

      // The navId on this child's route data. NOTE: Angular's default
      // `paramsInheritanceStrategy: 'emptyOnly'` copies a parent's `data`
      // (incl. `navId`) onto an EMPTY-PATH child snapshot — so the
      // `{ path: '', component }` page route that a feature mounts under a
      // navId-carrying segment SEES the inherited navId here. Reading it
      // directly is therefore NOT enough to tell "own" from "inherited".
      const dataNavId = (child.data as { navId?: string })?.navId;
      // Inherit the nearest declared navId down the chain (for sibling work).
      const childNavId = dataNavId ?? navId;

      // Primary crumb label: the resolver wins (explicit / dynamic /
      // custom-override route data). When the route carries NO breadcrumb
      // data at all, fall back to the label of the descriptor its `navId`
      // resolves to in the registry — the single source of truth for
      // migrated features that retired their hand-kept breadcrumb strings.
      // Dynamic crumbs (loading…/breadcrumbKey/:param/setLabel) always have
      // a non-null resolver result, so they are untouched.
      //
      // The fallback is gated on a NON-EMPTY `routeURL`: an empty-path
      // child occupies the SAME url as its parent, and (per the inheritance
      // note above) inherits the parent's navId — emitting a fallback crumb
      // there would duplicate the parent's crumb (Bug 5: the CIAM
      // `organizations` → `{ path: '' }` list page produced two identical
      // "Organizations" crumbs). A real navId-labelled segment always
      // contributes its own url segment, so this never suppresses a wanted
      // crumb.
      let label = breadcrumbService.getLabelForRoute(child);
      if (!label && dataNavId && routeURL) {
        label = this.registry?.descriptor(dataNavId)?.label ?? null;
      }
      if (label) {
        built.push({
          item: {
            label,
            url,
            queryParams: child.queryParams,
          },
          navId: childNavId,
          baseUrl: url,
        });
      }

      return this.buildCrumbs(child, breadcrumbService, url, built, childNavId);
    }

    return built;
  }

  /**
   * Post-walk enrichment (auto mode): attach STRUCTURAL siblings to every
   * crumb whose owning navId has breadcrumb-surface registry children, and
   * synthesize the active-tab crumb for tabbed detail pages (e.g.
   * installations) by matching the deepest primary route segment against
   * the parent's breadcrumb-surface children. Siblings are filtered to the
   * pre-resolved allowed-id set. Data siblings (Phase-3 commit 3) merge in
   * on top of these. Mutates `items` in place.
   */
  protected enrichWithRegistrySiblings(
    root: ActivatedRouteSnapshot,
    built: BuiltCrumb[],
    items: TwBreadcrumbItem[],
  ): void {
    // navId aligned per `items` index — extended when the active-tab crumb
    // is synthesized; consumed by the data-sibling merge.
    const navIdByIndex: (string | undefined)[] = built.map((b) => b.navId);
    const registry = this.registry;
    const allowed = this.allowedNavIds();

    if (registry) {
      // 1) Active-tab synthesis. Find the deepest primary route + the
      //    nearest navId whose registry children declare a matching path.
      const deepest = this.deepestPrimary(root);
      const deepNavId = this.activeTrail?.navIdFor(deepest);
      if (deepNavId) {
        const tabChildren = registry.childrenForSurface(deepNavId, 'breadcrumb');
        if (tabChildren.length > 0) {
          // The active tab segment is NOT necessarily the deepest primary
          // route's own url: a lazily-loaded tab feature mounts its page at
          // `{ path: '', component }`, so the deepest leaf carries an EMPTY
          // url and `dashboard`/`products`/… sits one level up. Derive the
          // active segment from the FULL accumulated primary url relative to
          // the owning crumb's baseUrl instead — that is robust to any
          // number of empty (`path: ''`) child routes below the tab.
          //
          // Anchor on the DEEPEST built crumb carrying `deepNavId` (the
          // detail context — e.g. the `/installations/:id` matcher crumb),
          // NOT the first. The same navId may now sit on MULTIPLE ancestor
          // routes: the app-shell route that owns the `installations` url
          // fragment (so the navId→descriptor-label fallback emits the
          // parent "Installations" crumb) AND the detail matcher route.
          // `find` returned the shallow parent crumb (baseUrl
          // `/installations`), against which the active segment resolves to
          // `:id` and matches no tab path — the synthesis silently dropped.
          // The deepest carrier's baseUrl is the detail url
          // (`/installations/:id`), so the tab segment is the first url part
          // beyond it regardless of where the navId is declared.
          const owner = [...built].reverse().find((b) => b.navId === deepNavId);
          const segmentBase = owner?.baseUrl ?? '';
          const fullUrl = this.primaryUrl(root);
          // Tab path candidates: the url segments after the owner's base.
          const rest = fullUrl.startsWith(segmentBase)
            ? fullUrl.slice(segmentBase.length).replace(/^\//, '')
            : '';
          const restSegments = rest ? rest.split('/') : [];
          // Match the FIRST url segment after the base to a tab child path
          // (tab paths are single relative segments, e.g. `dashboard`). Fall
          // back to the legacy whole-deepest-segment match for any tab whose
          // path itself spans multiple segments.
          const firstSeg = restSegments[0] ?? '';
          const deepestSeg = deepest?.url.map((s) => s.path).join('/') ?? '';
          const activeChild =
            tabChildren.find((c) => c.path === firstSeg) ??
            tabChildren.find((c) => c.path === deepestSeg);
          // Only synthesize when the active segment maps to a known tab
          // AND no existing crumb already represents it (route carried no
          // breadcrumb for the tab — the documented installations case).
          const alreadyCrumbed = built.some((b) => b.navId === activeChild?.id);
          if (activeChild && !alreadyCrumbed) {
            const siblings = this.mapStructuralSiblings(
              tabChildren,
              segmentBase,
              activeChild.id,
              allowed,
            );
            items.push({
              label: activeChild.label,
              url: `${segmentBase}/${activeChild.path}`,
              siblings,
              current: true,
            });
            navIdByIndex.push(activeChild.id);
          }
        }
      }

      // 2) Structural siblings for crumbs whose owning navId has
      //    breadcrumb-surface registry children (e.g. a tab crumb that DID
      //    carry route breadcrumb data).
      for (let i = 0; i < built.length; i++) {
        const b = built[i];
        if (!b.navId) {
          continue;
        }
        // A tab crumb's siblings = its PARENT's breadcrumb children.
        const parentId = this.parentNavIdOf(registry, b.navId);
        if (!parentId) {
          continue;
        }
        const siblingDescriptors = registry.childrenForSurface(parentId, 'breadcrumb');
        if (siblingDescriptors.length === 0) {
          continue;
        }
        // Anchor on the DEEPEST built crumb carrying `parentId`, not the
        // first — the same navId may sit on multiple ancestor routes (the
        // app-shell segment that owns the url fragment AND the detail
        // matcher). The shallow `find` returned the parent-list crumb
        // (baseUrl `/products`), making sibling urls like `/products/analytics`
        // instead of `/products/:id/analytics`. Mirrors the step-1 active-tab
        // synthesis anchor fix (commit 7eecf08d).
        const owner = [...built].reverse().find((x) => x.navId === parentId);
        const segmentBase = owner?.baseUrl ?? '';
        items[i] = {
          ...items[i],
          siblings: this.mapStructuralSiblings(
            siblingDescriptors,
            segmentBase,
            b.navId,
            allowed,
          ),
        };
      }
    }

    // 3) DATA siblings — merge each provider's normalized entries into the
    //    crumb whose owning navId matches the provider's `navId`. Carries
    //    `current`; merges ALONGSIDE any structural siblings already set.
    if (this.normalizedProviders.length > 0) {
      this.mergeDataSiblings(items, navIdByIndex);
    }
  }

  /**
   * Merge SIBLING_PROVIDER (data) siblings into crumbs by navId. For each
   * provider with a `navId` matching a crumb's owning navId, map its
   * `SiblingNavEntry`s → `TwBreadcrumbItem`s (carrying `current`) and
   * append them to that crumb's `siblings` (alongside structural ones).
   */
  private mergeDataSiblings(
    items: TwBreadcrumbItem[],
    navIdByIndex: (string | undefined)[],
  ): void {
    for (const provider of this.normalizedProviders) {
      if (!provider.navId) {
        continue;
      }
      const idx = navIdByIndex.indexOf(provider.navId);
      if (idx < 0) {
        continue;
      }
      const entries = provider.siblings();
      if (entries.length === 0) {
        continue;
      }
      const mapped = entries.map<TwBreadcrumbItem>((e: SiblingNavEntry) => ({
        label: e.label,
        url: e.url,
        current: e.current,
      }));
      const existing = items[idx].siblings ?? [];
      items[idx] = {
        ...items[idx],
        siblings: [...existing, ...mapped],
      };
    }
  }

  /** Map registry children → sibling crumb items, security-filtered. */
  private mapStructuralSiblings(
    children: NavDescriptor[],
    segmentBase: string,
    currentId: string,
    allowed: ReadonlySet<string>,
  ): TwBreadcrumbItem[] {
    return children
      .filter((c) => allowed.has(c.id))
      .map((c) => ({
        label: c.label,
        url: `${segmentBase}/${c.path}`,
        current: c.id === currentId,
      }));
  }

  /** Find the navId of the registry node that owns `childId` as a child. */
  private parentNavIdOf(registry: NavRegistry, childId: string): string | undefined {
    for (const node of registry.tree()) {
      if (node.children?.some((c) => c.id === childId)) {
        return node.id;
      }
    }
    return undefined;
  }

  /** Walk to the deepest primary-outlet activated snapshot. */
  private deepestPrimary(root: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let node = root;
    for (;;) {
      const next = node.children.find((c) => c.outlet === 'primary');
      if (!next) {
        return node;
      }
      node = next;
    }
  }

  /**
   * The full accumulated primary-outlet url (leading `/`, no trailing
   * slash) — every primary child's url segments joined. Unlike the
   * deepest leaf's own `url`, this survives `{ path: '' }` child routes
   * (lazy tab features mount at an empty path), so the active-tab segment
   * can be recovered relative to the owning crumb's baseUrl.
   */
  private primaryUrl(root: ActivatedRouteSnapshot): string {
    const segments: string[] = [];
    let node: ActivatedRouteSnapshot | undefined = root;
    while (node) {
      for (const s of node.url) {
        segments.push(s.path);
      }
      node = node.children.find((c) => c.outlet === 'primary');
    }
    return segments.length ? `/${segments.join('/')}` : '';
  }

  /** Pick the icon for a segment in the `icon` variant. */
  protected iconFor(item: TwBreadcrumbItem, index: number): string {
    return item.icon ?? this.iconFallbacks[Math.min(index, this.iconFallbacks.length - 1)];
  }

  /**
   * Navigate to a sibling/crumb from a dropdown menu item. Bound to the
   * `tw-dropdown-item`'s `(selected)` output so the WHOLE padded menu row
   * (not just an inner text anchor) is the navigable hit target — clicks on
   * the row padding and keyboard activation (Enter/Space, which the menu
   * dispatches as a click on the focused menuitem) both land here. The
   * dropdown-item closes the menu itself after `(selected)` fires.
   *
   * Mirrors `routerLink` semantics: the url is passed as a single command so
   * Angular resolves it against the app root (the breadcrumb urls are
   * absolute, leading-`/`), and the optional query-param map is forwarded.
   * No-ops when the router is absent (auth-less test/host contexts).
   */
  protected navigateTo(item: TwBreadcrumbItem): void {
    if (!item.url) {
      return;
    }
    void this.router?.navigate([item.url], {
      queryParams: item.queryParams ?? null,
    });
  }
}
